<template>
  <div>
    <e-filters
      :title="$t('Filtros')"
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <e-store-combo
            id="opening-hours-filter-store"
            v-model="filters.storeId"
            :required="false"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      ref="table-card"
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-row>
        <b-col md="12">
          <b-table
            id="opening-hours-table"
            class="table-responsive bordered"
            responsive
            striped
            show-empty
            :empty-text="$t('Nenhum horário encontrado.')"
            :items="rows"
            :fields="fields"
          >
            <template #cell(actions)="row">
              <e-grid-actions
                :show-delete="false"
                :show-update="$can('Update', 'Store')"
                @update="edit(row.item)"
              />
            </template>

            <template #cell(scope)="{ item }">
              <b-badge :variant="item.scope === 'general' ? 'light-primary' : 'light-secondary'">
                {{ item.scope === 'general' ? $t('Geral') : $t('Loja') }}
              </b-badge>
            </template>

            <template #cell(name)="{ item }">
              <feather-icon
                :icon="item.scope === 'general' ? 'GlobeIcon' : 'HomeIcon'"
                class="mr-50"
              />
              <strong>{{ item.scope === 'general' ? $t('Geral (todas as lojas)') : item.storeName }}</strong>
            </template>

            <template #cell(summary)="{ item }">
              <span :class="{ 'text-muted': !item.hasHours }">{{ summarizeDays(item.days) }}</span>
            </template>

            <template #cell(situation)="{ item }">
              <b-badge :variant="situationVariant(item)">
                {{ situationLabel(item) }}
              </b-badge>
            </template>
          </b-table>

          <b-row>
            <b-col md="6">
              <e-pagination-summary
                :current-page="paging.currentPage"
                :per-page="paging.pageSize"
                :total="paging.rowCount"
                :total-on-page="paging.rowsInCurrentPage"
              />
            </b-col>
            <b-col md="6">
              <b-pagination
                v-model="paging.currentPage"
                align="right"
                :total-rows="paging.rowCount"
                :per-page="paging.pageSize"
                aria-controls="opening-hours-table"
                @change="pageChange"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card-actions>

    <FAB
      v-if="$can('Update', 'Store')"
      :main-tooltip="$t('Configurar horário de uma loja')"
      @click="newStoreHours"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { BTable, BRow, BCol, BBadge, BPagination } from 'bootstrap-vue'
import { FAB, EFilters, EPageSizeSelector, EPaginationSummary } from '@/views/components'
import { alerts } from '@/mixins'
import EGridActions from '@/views/components/EGridActions.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'

/**
 * Tela de listagem de "Horários de Funcionamento" (D27): ponto de entrada em
 * `/lojas/horarios-funcionamento`. Mostra o horário Geral (fixo no topo) e as lojas que têm
 * horário PRÓPRIO — paginadas no padrão do sistema — com filtro por loja. Cada linha leva à tela
 * de manutenção (`opening-hours-manage`) já no escopo escolhido.
 */
export default {
  components: {
    BCardActions,
    BTable,
    BRow,
    BCol,
    BBadge,
    BPagination,
    FAB,
    EFilters,
    EPageSizeSelector,
    EPaginationSummary,
    EGridActions,
    EStoreCombo,
  },

  mixins: [alerts],

  data() {
    return {
      fetching: false,
      filters: {
        storeId: null,
      },
    }
  },

  computed: {
    ...mapState('pages/store/openingHours', ['scopeSummary', 'generalSummary', 'paging']),

    // Geral sempre no topo (fixa, fora da paginação); as lojas próprias vêm paginadas do servidor.
    rows() {
      const general = this.generalSummary ? [this.generalSummary] : []
      return [...general, ...this.scopeSummary]
    },

    fields() {
      return [
        {
          key: 'actions',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '80px' },
        },
        {
          key: 'scope',
          label: this.$t('Escopo'),
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          key: 'name',
          label: this.$t('Horário'),
        },
        {
          key: 'summary',
          label: this.$t('Dias e horários'),
        },
        {
          key: 'situation',
          label: this.$t('Situação'),
          class: 'text-center',
          thStyle: { width: '160px' },
        },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/store/openingHours', [
      'fetchScopeSummary',
      'setCurrentPage',
      'setPageSize',
    ]),

    async getData() {
      try {
        this.fetching = true
        await this.fetchScopeSummary({ storeId: this.filters.storeId })
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.fetching = false
      }
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.filters.storeId = null
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    situationLabel(item) {
      if (item.scope === 'general') {
        return item.hasHours ? this.$t('Configurado') : this.$t('Não configurado')
      }
      return this.$t('Horário próprio')
    },

    situationVariant(item) {
      if (item.scope === 'general') {
        return item.hasHours ? 'light-success' : 'light-warning'
      }
      return 'light-success'
    },

    // Resumo abreviado dos dias/horários: agrupa dias consecutivos com a mesma janela.
    // Ex.: "Seg-Sex 09:00-18:00 · Sáb 09:00-13:00". Sem dias abertos → "Fechado".
    summarizeDays(days) {
      const labels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      const openDays = (days || [])
        .filter(day => day.openingTime && day.closingTime)
        .slice()
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek)

      if (!openDays.length) return this.$t('Fechado')

      const groups = []
      openDays.forEach(day => {
        const window = `${day.openingTime}-${day.closingTime}`
        const last = groups[groups.length - 1]
        if (last && last.window === window && day.dayOfWeek === last.end + 1) {
          last.end = day.dayOfWeek
        } else {
          groups.push({ start: day.dayOfWeek, end: day.dayOfWeek, window })
        }
      })

      return groups
        .map(group => {
          const dayPart =
            group.start === group.end
              ? labels[group.start]
              : `${labels[group.start]}-${labels[group.end]}`
          return `${dayPart} ${group.window}`
        })
        .join(' · ')
    },

    edit(item) {
      const query = { scope: item.scope }
      if (item.storeId) query.storeId = item.storeId
      this.$router.push({ name: 'opening-hours-manage', query })
    },

    // Abre a manutenção no escopo de loja (sem loja escolhida) para criar um horário próprio.
    newStoreHours() {
      this.$router.push({ name: 'opening-hours-manage', query: { scope: 'store' } })
    },
  },
}
</script>

<style></style>
